import React, { useMemo, useState } from 'react';
import { Trophy, Medal, Award as AwardIcon } from 'lucide-react';

/**
 * Section-wise leaderboard for the teacher dashboard.
 * Computes top-N students per (class_level, section) by average score across all results.
 */
const TeacherSectionLeaderboard = ({ students = [], results = [] }) => {
  // Aggregate scores per student
  const studentAvg = useMemo(() => {
    const map = new Map();
    results.forEach(r => {
      const sid = r.student_id;
      if (!sid) return;
      const score = Number(r.score) || 0;
      if (!map.has(sid)) map.set(sid, { total: 0, count: 0 });
      const e = map.get(sid);
      e.total += score;
      e.count += 1;
    });
    const out = {};
    map.forEach((v, sid) => {
      out[sid] = { avg: v.count > 0 ? Math.round(v.total / v.count) : 0, attempts: v.count };
    });
    return out;
  }, [results]);

  // Group students by class+section, attach avg, sort, take top 5 per group
  const groups = useMemo(() => {
    const byGroup = {};
    students.forEach(s => {
      const key = `${s.class_level}|${s.section || 'A'}`;
      if (!byGroup[key]) byGroup[key] = { class_level: s.class_level, section: s.section || 'A', students: [] };
      const stat = studentAvg[s.id] || { avg: 0, attempts: 0 };
      byGroup[key].students.push({
        id: s.id,
        name: s.name,
        roll_number: s.roll_number,
        avg: stat.avg,
        attempts: stat.attempts,
      });
    });
    Object.values(byGroup).forEach(g => {
      g.students.sort((a, b) => b.avg - a.avg || b.attempts - a.attempts);
      g.students = g.students.slice(0, 5);
    });
    // Sorted group keys
    return Object.values(byGroup).sort((a, b) => {
      if (a.class_level !== b.class_level) return String(a.class_level).localeCompare(String(b.class_level));
      return String(a.section).localeCompare(String(b.section));
    });
  }, [students, studentAvg]);

  const [activeKey, setActiveKey] = useState(null);
  const currentKey = activeKey || (groups[0] ? `${groups[0].class_level}|${groups[0].section}` : null);
  const current = groups.find(g => `${g.class_level}|${g.section}` === currentKey) || groups[0];

  if (groups.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="teacher-section-leaderboard-empty">
        <div className="px-6 py-4 border-b bg-gradient-to-r from-amber-500 to-orange-600 text-white flex items-center gap-2">
          <Trophy size={20} />
          <h3 className="font-semibold">Section Leaderboard</h3>
        </div>
        <p className="p-8 text-center text-gray-500">No students yet to rank.</p>
      </div>
    );
  }

  const medal = (idx) => {
    if (idx === 0) return <Trophy size={16} className="text-yellow-500" />;
    if (idx === 1) return <Medal size={16} className="text-gray-400" />;
    if (idx === 2) return <AwardIcon size={16} className="text-amber-700" />;
    return <span className="text-xs font-bold text-gray-400">#{idx + 1}</span>;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="teacher-section-leaderboard">
      <div className="px-6 py-4 border-b bg-gradient-to-r from-amber-500 to-orange-600 text-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Trophy size={20} />
          <h3 className="font-semibold">Section Leaderboard</h3>
        </div>
        <span className="text-xs opacity-80">Top 5 per section by average score</span>
      </div>
      {/* Section tabs */}
      <div className="flex flex-wrap gap-2 px-6 py-3 bg-gray-50 border-b">
        {groups.map(g => {
          const key = `${g.class_level}|${g.section}`;
          const isActive = key === currentKey;
          return (
            <button
              key={key}
              onClick={() => setActiveKey(key)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${isActive ? 'bg-orange-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-orange-50'}`}
              data-testid={`leaderboard-section-tab-${g.class_level}-${g.section}`}
            >
              Class {g.class_level} · {g.section}
              <span className="ml-1 opacity-70">({g.students.length})</span>
            </button>
          );
        })}
      </div>
      {/* Current section list */}
      <div className="divide-y">
        {(current?.students || []).map((st, idx) => (
          <div key={st.id} className="px-6 py-3 flex items-center justify-between hover:bg-orange-50/30" data-testid={`leaderboard-row-${st.id}`}>
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-7 flex items-center justify-center">{medal(idx)}</div>
              <div>
                <p className="font-medium text-gray-800">{st.name}</p>
                <p className="text-xs text-gray-500">Roll {st.roll_number} · {st.attempts} attempt{st.attempts === 1 ? '' : 's'}</p>
              </div>
            </div>
            <div className="text-right">
              <span className={`text-lg font-bold ${st.avg >= 60 ? 'text-emerald-600' : st.avg >= 35 ? 'text-amber-600' : 'text-rose-600'}`}>
                {st.attempts > 0 ? `${st.avg}%` : '—'}
              </span>
            </div>
          </div>
        ))}
        {(current?.students || []).length === 0 && (
          <p className="p-6 text-center text-gray-400 text-sm">No students in this section yet.</p>
        )}
      </div>
    </div>
  );
};

export default TeacherSectionLeaderboard;
